// ============================================================================
// VideoGallerySection — Phase 15
// ----------------------------------------------------------------------------
// Reusable, slug-driven video section. Wired into 4 pages:
//
//   /business                       → slug="business-video"
//   /life-legacy                    → slug="life-video"
//   /aesthetic-clinic-photography   → slug="clinic-video"
//   /portfolio                      → slug="portfolio-video"
//
// Auto-hides while the matching Sanity gallery doc is unpublished or
// empty — that's the entire editorial workflow: Eli populates a clip in
// Studio and flips `published`, the page section appears. She empties
// or unpublishes, the section disappears. No FE deploy per page after
// this lands.
//
// Renders nothing (returns null, NOT an empty wrapper) when there are no
// items, so the surrounding page rhythm is unaffected — no orphan
// eyebrow, no extra section padding, no border-top hairline.
//
// Reuses the existing <MediaGallery> + PhotoSwipe lightbox pipeline. The
// item-level branching (image vs video tile) lives in MediaGallery itself
// (Phase 14) so this component stays declarative.
// ============================================================================

import React from "react";
import { Section, Kicker, SerifTitle, HairlineBrass } from "@/components/Section";
import { MediaGallery } from "@/components/MediaGallery";

/**
 * Section spec: video sections sit below the page's primary stills
 * "Selected work" gallery. Headings default to a calm uniform pair —
 * eyebrow "VIDEO" and H2 "Motion" — that reads well across all four
 * pages without per-page customization. Eli can rename per page in a
 * later round if she wants page-specific motion-section copy.
 *
 * Sizing (Phase 18.1 — Motion enlargement):
 *   Motion videos render in a centered, feature-sized frame — capped
 *   to `maxWidth` (default 960px ≈ a wide editorial column) so a single
 *   clip never lives in a narrow left tile next to empty space. Column
 *   count auto-adapts to the number of clips Eli has published:
 *     • 1 video  → 1 col mobile + 1 col desktop  (single, large, centered)
 *     • 2 videos → 1 col mobile + 2 col desktop  (still feature-sized)
 *     • 3+ videos → 2 col mobile + 2 col desktop (caller can override via
 *                   `desktopColumns` if she ever wants tighter 3-col)
 *   Callers can still hard-override columns and width via props.
 *
 * Props:
 *   - items         (array)   Output of useBusinessVideo / useLifeVideo /
 *                             useClinicVideo / usePortfolioVideo. Empty
 *                             array → section returns null.
 *   - kicker        (string)  Optional eyebrow override (default 'VIDEO').
 *   - title         (string)  Optional H2 override (default 'Motion').
 *   - supportingCopy (string) Optional supporting paragraph next to the
 *                             title. Hidden when empty.
 *   - inquiryType   (string)  Forwarded to MediaGallery so the lightbox
 *                             "Request a shoot like this" CTA can prefill
 *                             the inquiry form with the right context
 *                             (e.g. 'Business Brand Video'). Optional.
 *   - testId        (string)  Optional data-testid for the section root.
 *   - galleryId     (string)  Optional id for the gallery instance,
 *                             used as a stable key for the
 *                             MediaGallery -> PhotoSwipe scope.
 *   - className     (string)  Optional override classes for the Section.
 *   - desktopColumns (number) Optional column count override for masonry
 *                             on desktop. Defaults to an auto value based
 *                             on `items.length`.
 *   - mobileColumns  (number) Optional column count override on mobile.
 *                             Defaults to an auto value based on
 *                             `items.length` (1 for single/double, 2 for
 *                             3+).
 *   - maxWidth       (string) Optional Tailwind max-width class for the
 *                             gallery container. Defaults to 'max-w-[960px]'.
 */
export function VideoGallerySection({
    items,
    kicker = "Video",
    title = "Motion",
    supportingCopy,
    inquiryType,
    testId,
    galleryId,
    className,
    desktopColumns,
    mobileColumns,
    maxWidth = "max-w-[960px]",
}) {
    // ── Auto-hide ──────────────────────────────────────────────────────
    // Returns null entirely (NOT an empty wrapper) so the surrounding
    // page rhythm collapses cleanly when the Sanity doc is unpublished
    // or empty. No orphan eyebrow, no extra spacing, no top hairline.
    if (!Array.isArray(items) || items.length === 0) return null;

    // ── Auto column count ──────────────────────────────────────────────
    // Single clip stays full-width; two clips sit side-by-side on desktop
    // but stack on mobile; 3+ goes to a tighter 2-col grid. Callers can
    // override either prop explicitly.
    const count = items.length;
    const autoDesktopCols = count === 1 ? 1 : 2;
    const autoMobileCols = count >= 3 ? 2 : 1;
    const resolvedDesktopCols = desktopColumns ?? autoDesktopCols;
    const resolvedMobileCols = mobileColumns ?? autoMobileCols;

    return (
        <Section
            spacing="normal"
            className={className}
            data-testid={testId || "video-gallery-section"}
        >
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-10">
                <div className="lg:col-span-7">
                    <Kicker>{kicker}</Kicker>
                    <SerifTitle className="mt-3">{title}</SerifTitle>
                    <HairlineBrass className="mt-6" />
                </div>
                {supportingCopy ? (
                    <p className="lg:col-span-5 text-sm leading-[1.85] text-muted-foreground max-w-[52ch]">
                        {supportingCopy}
                    </p>
                ) : null}
            </div>
            {/* Centered, capped-width container so single/double-clip
              * Motion sections render as wide editorial features instead
              * of narrow left-aligned tiles. `mx-auto` centers on every
              * viewport; `maxWidth` is responsive-friendly (Tailwind
              * arbitrary value defaults to 960px). */}
            <div className={`mx-auto w-full ${maxWidth}`}>
                <MediaGallery
                    items={items}
                    initialCount={8}
                    incrementCount={8}
                    desktopColumns={resolvedDesktopCols}
                    mobileColumns={resolvedMobileCols}
                    galleryId={galleryId || "video-gallery"}
                    defaultInquiryType={inquiryType || ""}
                />
            </div>
        </Section>
    );
}

export default VideoGallerySection;
